import { motion, useReducedMotion } from 'motion/react';
import { type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { useConnectCalendar } from '@/integrations/useConnectCalendar';

import { meetingsSidebarStore } from './meetings-sidebar.store';

const ROWS = [
  { y: 8, fillOpacity: 0.1 },
  { y: 26, fillOpacity: 0.14 },
  { y: 44, fillOpacity: 0.18 },
];

const EASE_OUT_QUINT = [0.22, 1, 0.36, 1] as const;

const MeetingsEmptyStateAnimation = () => {
  const shouldReduceMotion = useReducedMotion();
  return (
    <svg
      viewBox="0 0 180 60"
      className="w-40 h-[3.375rem] text-foreground-muted overflow-visible"
      aria-hidden
    >
      {ROWS.map((row, i) => {
        const rowDelay = 0.05 + i * 0.12;
        return (
          <g key={i}>
            <motion.circle
              cx={14}
              cy={row.y + 5}
              r={4}
              fill="currentColor"
              fillOpacity={row.fillOpacity + 0.04}
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={
                shouldReduceMotion
                  ? undefined
                  : {
                      duration: 0.25,
                      delay: rowDelay,
                      ease: EASE_OUT_QUINT,
                    }
              }
            />
            <motion.rect
              x={24}
              y={row.y}
              width={140}
              height={10}
              rx={3}
              fill="currentColor"
              fillOpacity={row.fillOpacity}
              stroke="currentColor"
              strokeOpacity={0.15}
              strokeWidth={0.75}
              initial={shouldReduceMotion ? false : { opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={
                shouldReduceMotion
                  ? undefined
                  : {
                      duration: 0.45,
                      delay: rowDelay + 0.04,
                      ease: EASE_OUT_QUINT,
                    }
              }
              style={{
                transformBox: 'fill-box',
                transformOrigin: 'left center',
              }}
            />
          </g>
        );
      })}
    </svg>
  );
};

export function MeetingsEmptyState({
  showConnectCalendar,
}: {
  showConnectCalendar: boolean;
}) {
  const { isConnecting, connect } = useConnectCalendar();

  return (
    <Empty.Root>
      <Empty.Media>
        <MeetingsEmptyStateAnimation />
      </Empty.Media>
      <Empty.Header>
        <Empty.Title>No meeting notes yet</Empty.Title>
        <Empty.Description>
          Start a meeting and Littlebird will capture the transcript and a
          written summary here.
        </Empty.Description>
      </Empty.Header>
      {showConnectCalendar && (
        <Empty.Actions>
          <Button
            onClick={connect}
            isLoading={isConnecting}
            disabled={isConnecting}
          >
            <Icons.calendarPlus />
            Connect calendar
          </Button>
        </Empty.Actions>
      )}
    </Empty.Root>
  );
}

function IconEmptyState({
  icon,
  title,
  description,
  actions,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Empty.Root>
      <Empty.Media variant="icon">{icon}</Empty.Media>
      <Empty.Header>
        <Empty.Title>{title}</Empty.Title>
        <Empty.Description>{description}</Empty.Description>
      </Empty.Header>
      {actions && <Empty.Actions>{actions}</Empty.Actions>}
    </Empty.Root>
  );
}

export function MeetingsSearchEmptyState({ query }: { query: string }) {
  return (
    <IconEmptyState
      icon={<Icons.search />}
      title="No meetings found"
      description={
        <>
          No meetings match &ldquo;
          {query.length > 40
            ? `${query.slice(0, 20)}…${query.slice(-19)}`
            : query}
          &rdquo;.
        </>
      }
    />
  );
}

export function MeetingsProjectEmptyState({
  noProject,
}: {
  noProject: boolean;
}) {
  if (noProject) {
    return (
      <IconEmptyState
        icon={<Icons.circleDashed />}
        title="No meeting notes without a project"
        description="Meeting notes that aren't in a project show up here."
      />
    );
  }

  return (
    <IconEmptyState
      icon={<Icons.folder />}
      title="No meeting notes yet"
      description={
        <>
          Use{' '}
          <span className="whitespace-nowrap text-foreground-subtle">
            Add to project
          </span>{' '}
          on any meeting to keep its notes here.
        </>
      }
      actions={
        <Button onClick={() => meetingsSidebarStore.clearSlice()}>
          Show all meetings
        </Button>
      }
    />
  );
}

export function MeetingsNoUpcomingEventsEmptyState() {
  return (
    <IconEmptyState
      icon={<Icons.calendar />}
      title="No upcoming events"
      description="Upcoming meetings from your calendar will appear here."
    />
  );
}
